import { formatDate } from "@plasma/utils/time";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  locationSalesGetOrderSalesReportData,
  locationSalesGetOrderTypeReportData,
  type OrderTypeEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

import { billsFromOrders } from "./orders-view-helpers";

type OrderTypeData = OrderTypeEntry;

type ExtendedBill = ReturnType<typeof billsFromOrders>[number];

interface OrdersViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const OrdersView = ({
  locationId,
  dateRange,
  tableId,
}: OrdersViewProps) => {
  const dataResult = useQuery(locationSalesGetOrderSalesReportData, {
    dateRange: {
      from: dateRange.from.getTime(),
      to: dateRange.to.getTime(),
    },
    locationId: toConvexTableId("locations", locationId),
    paginationOpts: { numItems: 500, cursor: null },
  });
  const data = useMemo(() => dataResult?.page ?? [], [dataResult?.page]);
  const isLoading = dataResult === undefined;

  // Get order type summary data
  const orderTypeData =
    useQuery(locationSalesGetOrderTypeReportData, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    }) ?? [];
  const isOrderTypeLoading = orderTypeData === undefined;

  const bills = useMemo(() => billsFromOrders(data), [data]);
  const columnHelper = createColumnHelper<ExtendedBill>();

  // Order Type Summary columns
  const orderTypeColumnHelper = createColumnHelper<OrderTypeData>();

  const orderTypeSummaryColumns = useMemo(
    () =>
      [
        orderTypeColumnHelper.accessor("location", {
          header: "Location",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.location.id),
          meta: {
            faceting: {
              title: "Location",
              getOption: (r: OrderTypeData["location"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: OrderTypeData["location"]) => r.name,
          },
        }),
        orderTypeColumnHelper.accessor("type", {
          header: "Order Type",
          meta: {
            faceting: {
              title: "Order Type",
              getOption: (t: OrderTypeData["type"]) => ({
                value: t,
                label: t,
              }),
            },
          },
        }),
        orderTypeColumnHelper.accessor("itemsSold", {
          header: "Items Sold",
          cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
          meta: {
            getCellForExport: (d: OrderTypeData["itemsSold"]) => d,
          },
        }),
        orderTypeColumnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: OrderTypeData["amountSold"]) => d,
          },
        }),
      ] as ColumnDef<OrderTypeData>[],
    [orderTypeColumnHelper]
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("id", {
          header: "Bill ID",
          cell: ({ getValue }) => getValue(),
        }),
        columnHelper.accessor("employee", {
          header: "Employee",
          cell: ({ getValue }) => {
            const employee = getValue();
            return employee
              ? `${employee.firstName} ${employee.lastName}`
              : "N/A";
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.employee
              ? filterValue.includes(original.employee._id)
              : false,
          meta: {
            faceting: {
              title: "Employee",
              getOption: (employee: ExtendedBill["employee"]) => ({
                value: employee?._id ?? "",
                label: employee
                  ? `${employee.firstName} ${employee.lastName}`
                  : "N/A",
              }),
            },
          },
        }),
        columnHelper.accessor("session", {
          header: "POS Device",
          cell: ({ getValue }) => {
            const session = getValue();
            return session
              ? `${session.deviceInfo.model} (${session.deviceInfo.os})`
              : "Unknown Device";
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.session
              ? filterValue.includes(original.session.deviceId)
              : false,
          meta: {
            faceting: {
              title: "POS Device",
              getOption: (session: ExtendedBill["session"]) => ({
                value: session?.deviceId ?? "",
                label: session
                  ? `${session.deviceInfo.model} (${session.deviceInfo.os})`
                  : "Unknown Device",
              }),
            },
          },
        }),
        columnHelper.accessor("order", {
          header: "Order Type",
          cell: ({ getValue }) => getValue().type,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.order.type),
          meta: {
            faceting: {
              title: "Order Type",
              getOption: (order: ExtendedBill["order"]) => ({
                value: order.type,
                label: order.type,
              }),
            },
          },
        }),
        columnHelper.accessor("bill", {
          header: "Amount",
          cell: ({ getValue }) => {
            const bill = getValue();
            return `$${bill.total.toFixed(2)}`;
          },
          meta: {
            getCellForExport: (bill: ExtendedBill["bill"]) => bill.total,
          },
        }),
        columnHelper.accessor("order.createdAt", {
          header: "Date",
          cell: ({ getValue }) => formatDate(new Date(getValue()), "M/D/YYYY"),
          meta: {
            getCellForExport: (date: number) => new Date(date).toISOString(),
          },
        }),
      ] as ColumnDef<ExtendedBill>[],
    [columnHelper]
  );

  if (isLoading || isOrderTypeLoading) {
    return <LoadingPanel message="Loading order data..." />;
  }

  return (
    <div className="space-y-8">
      {/* Order Type Summary */}
      <div className="space-y-4">
        <h3 className="subtitle text-foreground">Order Type Summary</h3>
        <p className="body-sm text-muted-foreground">
          Aggregated sales data grouped by order type (dine-in, take-out, etc.)
        </p>
        <SalesDataTable
          columns={orderTypeSummaryColumns}
          data={orderTypeData}
          id={`${tableId}-order-types`}
        />
      </div>

      {/* Detailed Orders */}
      <div className="space-y-4">
        <h3 className="subtitle text-foreground">
          Detailed Order Transactions
        </h3>
        <p className="body-sm text-muted-foreground">
          Individual bill-level transactions with employee and device details
        </p>
        <SalesDataTable
          columns={columns}
          data={bills}
          id={`${tableId}-bills`}
        />
      </div>
    </div>
  );
};
